import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

/**
 * Severity shades the bar by how far the value sits from the benchmark,
 * neutral is grey whatever the gap, and comparison is plain green or red for
 * any gap at all.
 */
export const GapBarColorScheme = {
  Severity: "severity",
  Neutral: "neutral",
  Comparison: "comparison",
} as const;
export type GapBarColorScheme =
  (typeof GapBarColorScheme)[keyof typeof GapBarColorScheme];

interface GapBarProps {
  /** Rep's score */
  value: number;
  /** Benchmark score */
  benchmark: number;
  /** Value range [min, max] - defaults to [0, 100] for backward compatibility */
  range?: readonly [number, number];
  /** Visual size */
  size?: "sm" | "md" | "lg";
  /** Show helper ticks at ±10/±20 pts */
  showTicks?: boolean;
  /** Delta range around benchmark that maps to the edge fill (e.g. 50 => ±25) */
  scale?: number;
  /** Show the numeric delta label above the bar */
  showDelta?: boolean;
  /** Render the comparison bar in addition to the numeric delta label. */
  showBar?: boolean;
  /** Optional styling for the numeric delta label. */
  deltaClassName?: string;
  /** Optional className to position the component */
  className?: string;
  /** How the bar and delta are colored; defaults to severity. */
  colorScheme?: GapBarColorScheme;
  /** Invert color polarity (below benchmark = green, above = red) */
  lowerIsBetter?: boolean;
  /** Prefix for the delta label (e.g. "$" for currency) */
  deltaPrefix?: string;
  /** Override for decimal places in the delta label */
  deltaDecimalPlaces?: number;
  /**
   * Custom formatter for the absolute delta value. When provided, replaces
   * the default `${deltaPrefix}${|delta|}` rendering — useful for durations
   * or other non-numeric units.
   */
  formatDelta?: (absoluteDelta: number) => string;
}

/**
 * GapBar — a compact diverging bar centered on the benchmark (0 gap).
 * - Arrow points right when value > benchmark, left when value < benchmark.
 * - Arrow length scales with |Δ| based on the specified range.
 * - Shading intensity steps up at different thresholds.
 * - Always prints a tabular, signed delta in the original scale units.
 * - Supports different value ranges via the `range` prop (defaults to [0, 100]).
 *
 * Examples:
 * <GapBar value={79} benchmark={85} size="md" /> // 0-100 scale (default)
 * <GapBar value={3.2} benchmark={4.1} range={[1, 5]} size="md" /> // 1-5 scale
 */
export const GapBar: FC<GapBarProps> = (props) => {
  const {
    value,
    benchmark,
    range = [0, 100],
    size = "md",
    scale = 50,
    showDelta = true,
    showBar = true,
    deltaClassName,
    className,
    colorScheme = GapBarColorScheme.Severity,
    lowerIsBetter = false,
    deltaPrefix = "",
    deltaDecimalPlaces,
    formatDelta,
  } = props;
  const [min, max] = range;
  const rangeSize = max - min;

  // Helper functions for the specified range
  const clampToRange = (n: number) => Math.max(min, Math.min(max, n));
  const normalizeToPercent = (n: number) => ((n - min) / rangeSize) * 100;

  // Display precision
  const decimalPlaces = deltaDecimalPlaces ?? (rangeSize <= 1 ? 2 : 1);
  const factor = 10 ** decimalPlaces;

  // Original values (clamped to range)
  const originalValue = clampToRange(value ?? min);
  const originalBenchmark = clampToRange(benchmark ?? min);

  // Round to display precision, then compute delta from rounded values
  const displayValue = Math.round(originalValue * factor) / factor;
  const displayBenchmark = Math.round(originalBenchmark * factor) / factor;
  const originalDelta =
    Math.round((displayValue - displayBenchmark) * factor) / factor;

  // Normalized values for visual calculations (0-100)
  const v = normalizeToPercent(originalValue);
  const b = normalizeToPercent(originalBenchmark);

  // Visual delta for bar calculations (0-100 scale)
  const visualDeltaRaw = v - b;
  const sign = visualDeltaRaw >= 0 ? 1 : -1;
  const visualMagnitude = Math.min(Math.abs(visualDeltaRaw), 100); // 0..100

  // Map 0..100 pts to 0..50 viewBox units from center (because center→edge = 50)
  const magHalf = visualMagnitude * 0.5; // 0..50

  // Severity tiers for shading (based on visual magnitude)
  const severity =
    visualMagnitude >= 20
      ? "high"
      : visualMagnitude >= 10
        ? "med"
        : visualMagnitude >= 5
          ? "low"
          : "tiny";

  const isPositive = lowerIsBetter ? originalDelta <= 0 : originalDelta >= 0;
  const getGradientClass = (positive: boolean, mag: number): string => {
    if (colorScheme === GapBarColorScheme.Neutral) {
      return "bg-gradient-to-r from-slate-400 to-slate-500";
    }
    if (colorScheme === GapBarColorScheme.Comparison) {
      return positive
        ? "bg-gradient-to-r from-green-400 to-green-500"
        : "bg-gradient-to-r from-red-400 to-red-500";
    }
    if (mag < 5) return "bg-gradient-to-r from-slate-300 to-slate-400";
    if (positive) {
      if (mag < 10) return "bg-gradient-to-r from-lime-400 to-lime-600";
      if (mag < 20) return "bg-gradient-to-r from-green-400 to-green-600";
      if (mag < 35) return "bg-gradient-to-r from-emerald-400 to-emerald-600";
      return "bg-gradient-to-r from-emerald-600 to-emerald-800";
    }
    if (mag < 10) return "bg-gradient-to-r from-amber-400 to-amber-600";
    if (mag < 20) return "bg-gradient-to-r from-orange-400 to-orange-600";
    if (mag < 35) return "bg-gradient-to-r from-red-400 to-red-600";
    return "bg-gradient-to-r from-red-600 to-red-800";
  };

  const getSolidBgClass = (positive: boolean, mag: number): string => {
    if (colorScheme === GapBarColorScheme.Neutral) {
      return "bg-slate-500";
    }
    if (colorScheme === GapBarColorScheme.Comparison) {
      return positive ? "bg-green-500" : "bg-red-500";
    }
    if (mag < 5) return "bg-slate-400";
    if (positive) {
      if (mag < 10) return "bg-lime-600";
      if (mag < 20) return "bg-success";
      if (mag < 35) return "bg-emerald-600";
      return "bg-emerald-700";
    }
    if (mag < 10) return "bg-amber-600";
    if (mag < 20) return "bg-orange-600";
    if (mag < 35) return "bg-destructive";
    return "bg-red-700";
  };

  const getTextClass = (positive: boolean, mag: number): string => {
    if (colorScheme === GapBarColorScheme.Neutral) {
      return "text-slate-600";
    }
    if (colorScheme === GapBarColorScheme.Comparison) {
      if (mag === 0) return "text-muted-foreground";
      return positive ? "text-green-700" : "text-red-700";
    }
    if (mag < 5) return "text-muted-foreground";
    if (positive) {
      if (mag < 10) return "text-lime-700";
      if (mag < 20) return "text-green-700";
      if (mag < 35) return "text-emerald-600";
      return "text-emerald-700";
    }
    if (mag < 10) return "text-amber-700";
    if (mag < 20) return "text-orange-700";
    if (mag < 35) return "text-destructive";
    return "text-red-700";
  };

  const fillOpacity =
    severity === "high"
      ? 0.9
      : severity === "med"
        ? 0.85
        : severity === "low"
          ? 0.8
          : 0.75;
  const strokeOpacity =
    severity === "high"
      ? 0.95
      : severity === "med"
        ? 0.9
        : severity === "low"
          ? 0.85
          : 0.8;

  // Clean, minimal dimensions
  const barHeight = size === "lg" ? 8 : size === "sm" ? 4 : 6;
  const widthPx = size === "lg" ? 200 : size === "sm" ? 50 : 160;

  // Scale mapping: |delta| >= (scale/2) => fill reaches the nearest edge from benchmark
  const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 50;
  const halfRange = safeScale / 2; // delta that maps to full fill to edge
  // Map visual delta to an offset from the center (50%) so the centerline is always the middle
  const fillProportion = Math.min(Math.abs(visualDeltaRaw) / halfRange, 1); // 0..1
  const offsetFromCenterPercent = fillProportion * 50; // 0..50
  const fillLeftPercent = sign > 0 ? 50 : 50 - offsetFromCenterPercent;
  const fillWidthPercent = offsetFromCenterPercent;
  const fillEndPercent = 50 + sign * offsetFromCenterPercent; // where the dot should be

  // Color classes driven by visual magnitude and polarity
  const barGradientClass = getGradientClass(isPositive, visualMagnitude);
  const dotBgClass = getSolidBgClass(isPositive, visualMagnitude);
  const numberTextClass = getTextClass(isPositive, visualMagnitude);
  const deltaLabel = showDelta ? (
    <span
      className={twMerge(
        "mb-1 text-[10px] leading-none font-medium",
        numberTextClass,
        deltaClassName,
      )}
    >
      {originalDelta > 0 ? "+" : originalDelta < 0 ? "-" : ""}
      {formatDelta
        ? formatDelta(Math.abs(originalDelta))
        : `${deltaPrefix}${Math.abs(originalDelta)}`}
    </span>
  ) : null;

  if (!showBar) {
    return deltaLabel;
  }

  return (
    <div
      className={twMerge(
        "inline-flex flex-col items-center gap-0.5",
        className,
      )}
    >
      {/* Clean label above */}
      {showDelta && <div className="flex items-center gap-1">{deltaLabel}</div>}

      {/* Simplified progress bar */}
      <div
        className="relative rounded-full bg-gray-200"
        style={{ width: widthPx, height: barHeight }}
      >
        {/* Progress fill */}
        {visualMagnitude > 0 && (
          <div
            className={twMerge("absolute top-0", barGradientClass)}
            style={{
              left: `${fillLeftPercent}%`,
              width: `${fillWidthPercent}%`,
              height: barHeight,
              opacity: fillOpacity,
            }}
          />
        )}

        {/* Circle indicator */}
        {visualMagnitude > 0 && (
          <div
            className={twMerge("absolute rounded-full", dotBgClass)}
            style={{
              width: barHeight * 1.4,
              height: barHeight * 1.4,
              left: `${fillEndPercent}%`,
              top: -barHeight * 0.2,
              transform: "translateX(-50%)",
            }}
          />
        )}

        {/* Center marker (always middle of the bar) */}
        <div
          className="absolute top-0 w-[1.5px] rounded-full bg-gray-400"
          style={{
            left: "50%",
            transform: "translateX(-50%)",
            height: barHeight * 1.4,
            top: -barHeight * 0.2,
          }}
        />
      </div>
    </div>
  );
};

/**
 * Builds the "<prefix> is +N points above the median" sentence a gap bar's
 * tooltip shows. Exported so callers can render it inside their own tooltip
 * instead of the one `renderGapBarWithTooltip` bundles.
 */
export function buildGapBarTooltipContent(
  tooltipContentPrefix: string,
  scoreValue: number,
  benchmarkValue: number,
  options?: {
    benchmarkLabel?: string;
    range?: readonly [number, number];
    deltaPrefix?: string;
    deltaDecimalPlaces?: number;
    formatDelta?: (absoluteDelta: number) => string;
  },
): string {
  const rangeSize = (options?.range?.[1] ?? 100) - (options?.range?.[0] ?? 0);
  const tooltipDecimalPlaces =
    options?.deltaDecimalPlaces ?? (rangeSize <= 1 ? 2 : 1);
  const roundedScore = parseFloat(scoreValue.toFixed(tooltipDecimalPlaces));
  const roundedBenchmark = parseFloat(
    benchmarkValue.toFixed(tooltipDecimalPlaces),
  );
  const delta = parseFloat(
    Math.abs(roundedScore - roundedBenchmark).toFixed(tooltipDecimalPlaces),
  );

  const prefix = options?.deltaPrefix ?? "";
  const formattedDelta = options?.formatDelta
    ? options.formatDelta(delta)
    : `${prefix}${delta}`;
  const unit = options?.formatDelta || prefix ? "" : " points";

  const aboveOrBelowOrAt =
    scoreValue > benchmarkValue
      ? `${formattedDelta}${unit} above`
      : scoreValue < benchmarkValue
        ? `${formattedDelta}${unit} below`
        : "at";

  const benchmarkLabel = options?.benchmarkLabel ?? "median";
  return `${tooltipContentPrefix} is ${aboveOrBelowOrAt} the ${benchmarkLabel}`;
}

export function renderGapBarWithTooltip(
  tooltipContentPrefix: string,
  scoreValue: number,
  benchmarkValue: number,
  options?: {
    benchmarkLabel?: string;
    range?: [number, number];
    colorScheme?: GapBarColorScheme;
    lowerIsBetter?: boolean;
    deltaPrefix?: string;
    deltaDecimalPlaces?: number;
    formatDelta?: (absoluteDelta: number) => string;
  },
) {
  const tooltipContent = buildGapBarTooltipContent(
    tooltipContentPrefix,
    scoreValue,
    benchmarkValue,
    options,
  );

  return (
    <TooltipTrigger>
      <TooltipHoverTarget>
        <GapBar
          value={scoreValue}
          benchmark={benchmarkValue}
          range={options?.range}
          size="sm"
          colorScheme={options?.colorScheme}
          lowerIsBetter={options?.lowerIsBetter}
          deltaPrefix={options?.deltaPrefix}
          deltaDecimalPlaces={options?.deltaDecimalPlaces}
          formatDelta={options?.formatDelta}
        />
      </TooltipHoverTarget>
      <SimpleTooltip>{tooltipContent}</SimpleTooltip>
    </TooltipTrigger>
  );
}
